import {
	AnimatePresence,
	type MotionValue,
	motion,
	useMotionValue,
	useSpring,
	useTransform,
} from 'framer-motion'
import { useRef, useState } from 'react'
import { Link } from 'react-router'
import { cn } from '#app/utils/misc.tsx'

export interface FloatingDockItem {
	title: string
	icon: React.ReactNode
	href?: string
	onClick?: () => void
}

export interface FloatingDockProps {
	items: FloatingDockItem[]
	desktopClassName?: string
	mobileClassName?: string
}

const FloatingDock = ({
	items,
	desktopClassName,
	mobileClassName,
}: FloatingDockProps) => {
	return (
		<>
			<FloatingDockDesktop items={items} className={desktopClassName} />
			<FloatingDockMobile items={items} className={mobileClassName} />
		</>
	)
}

const FloatingDockMobile = ({
	items,
	className,
}: {
	items: FloatingDockItem[]
	className?: string
}) => {
	const [open, setOpen] = useState(false)

	return (
		<div className={cn('relative block md:hidden', className)}>
			<AnimatePresence>
				{open && (
					<motion.div
						layoutId="nav"
						className="absolute inset-x-0 bottom-full mb-2 flex flex-col gap-2"
					>
						{items.map((item, idx) => (
							<motion.div
								key={item.title}
								initial={{ opacity: 0, y: 10 }}
								animate={{
									opacity: 1,
									y: 0,
								}}
								exit={{
									opacity: 0,
									y: 10,
									transition: {
										delay: idx * 0.05,
									},
								}}
								transition={{ delay: (items.length - 1 - idx) * 0.05 }}
							>
								{item.href ? (
									<Link
										to={item.href}
										prefetch="intent"
										className="bg-surface/80 flex h-10 w-10 items-center justify-center rounded-full backdrop-blur-sm"
									>
										<div className="h-4 w-4">{item.icon}</div>
									</Link>
								) : (
									<button
										onClick={item.onClick}
										className="bg-surface/80 flex h-10 w-10 items-center justify-center rounded-full backdrop-blur-sm"
									>
										<div className="h-4 w-4">{item.icon}</div>
									</button>
								)}
							</motion.div>
						))}
					</motion.div>
				)}
			</AnimatePresence>
			<button
				onClick={() => setOpen(!open)}
				className="bg-surface/80 flex h-10 w-10 items-center justify-center rounded-full backdrop-blur-sm"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="24"
					height="24"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					className={cn(
						'h-5 w-5 text-neutral-400 transition-all',
						open && 'rotate-90',
					)}
				>
					<path d="M3 12h18" />
					<path d="M3 6h18" />
					<path d="M3 18h18" />
				</svg>
			</button>
		</div>
	)
}

const FloatingDockDesktop = ({
	items,
	className,
}: {
	items: FloatingDockItem[]
	className?: string
}) => {
	let mouseX = useMotionValue(Infinity)

	return (
		<motion.div
			onMouseMove={(e) => mouseX.set(e.pageX)}
			onMouseLeave={() => mouseX.set(Infinity)}
			className={cn(
				'bg-surface/50 mx-auto hidden h-16 items-end gap-4 rounded-2xl px-4 pb-3 backdrop-blur-md md:flex',
				'border border-white/10',
				'shadow-[0_0_40px_rgba(0,240,208,0.15)]',
				className,
			)}
		>
			{items.map((item) => (
				<IconContainer mouseX={mouseX} key={item.title} {...item} />
			))}
		</motion.div>
	)
}

function IconContainer({
	mouseX,
	title,
	icon,
	href,
	onClick,
}: {
	mouseX: MotionValue
	title: string
	icon: React.ReactNode
	href?: string
	onClick?: () => void
}) {
	let ref = useRef<HTMLDivElement>(null)

	let distance = useTransform(mouseX, (val) => {
		let bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 }
		return val - bounds.x - bounds.width / 2
	})

	let widthTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40])
	let heightTransform = useTransform(distance, [-150, 0, 150], [40, 80, 40])

	let widthTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20])
	let heightTransformIcon = useTransform(distance, [-150, 0, 150], [20, 40, 20])

	let width = useSpring(widthTransform, {
		mass: 0.1,
		stiffness: 150,
		damping: 12,
	})
	let height = useSpring(heightTransform, {
		mass: 0.1,
		stiffness: 150,
		damping: 12,
	})

	let widthIcon = useSpring(widthTransformIcon, {
		mass: 0.1,
		stiffness: 150,
		damping: 12,
	})
	let heightIcon = useSpring(heightTransformIcon, {
		mass: 0.1,
		stiffness: 150,
		damping: 12,
	})

	const [hovered, setHovered] = useState(false)

	const content = (
		<>
			<motion.div
				ref={ref}
				style={{ width, height }}
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
				className="bg-surface/80 hover:bg-surface relative flex aspect-square items-center justify-center rounded-full backdrop-blur-sm transition-colors"
			>
				<motion.div
					style={{ width: widthIcon, height: heightIcon }}
					className="flex items-center justify-center"
				>
					{icon}
				</motion.div>
			</motion.div>
			<AnimatePresence>
				{hovered && (
					<motion.div
						initial={{ opacity: 0, y: 10, x: '-50%' }}
						animate={{ opacity: 1, y: 0, x: '-50%' }}
						exit={{ opacity: 0, y: 2, x: '-50%' }}
						className="bg-surface/95 absolute -top-8 left-1/2 w-fit -translate-x-1/2 rounded-md border border-white/10 px-2 py-0.5 text-xs whitespace-pre text-neutral-100 backdrop-blur-sm"
					>
						{title}
					</motion.div>
				)}
			</AnimatePresence>
		</>
	)

	if (href) {
		return (
			<Link to={href} prefetch="intent" className="relative">
				{content}
			</Link>
		)
	}

	if (onClick) {
		return (
			<button onClick={onClick} className="relative">
				{content}
			</button>
		)
	}

	return <div className="relative">{content}</div>
}

export { FloatingDock, FloatingDockMobile, FloatingDockDesktop }
